import { useEffect, useState } from 'react';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';
import { money, shortDeptLabel, variancePct } from './globalReporting';
import GlobalRecordDetails from './GlobalRecordDetails';
import { useEmployeeDetailFilters } from './EmployeeDetailFilters';

// Trimmed copy of GA_Elements/DetailModalControls.js + GASummaryDashboard.js's
// DrillModalShell — same duplicate-per-dashboard convention as every other
// Elements file.

export function DetailIcon({ name }) {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {name === 'back' ? <path d="m12 5-7 7 7 7M5 12h14" />
            : name === 'close' ? <path d="m6 6 12 12M18 6 6 18" />
                : name === 'search' ? <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4 4" /></>
                    : <><circle cx="12" cy="8" r="3" /><path d="M5 21v-2a7 7 0 0 1 14 0v2" /></>}
    </svg>;
}

function DetailModalControls({ onBack, onClose }) {
    return <div className="esd-modal-actions">
        {onBack && <button type="button" className="esd-modal-action" onClick={onBack} aria-label="Back" title="Back"><DetailIcon name="back" /></button>}
        <button type="button" className="esd-modal-action" onClick={onClose} aria-label="Close details" title="Close"><DetailIcon name="close" /></button>
    </div>;
}

function DrillModalShell({ icon, title, sub, breadcrumb, onJump, onClose, maxWidth = 860, children }) {
    return (
        <div className="legal-modal-bg esd-drill-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal esd esd-drill-modal esd-compact-modal" style={{ maxWidth }}>
                <div className="legal-modal-hdr esd-drill-hdr">
                    <div className="legal-modal-icon esd-drill-icon">{icon}</div>
                    <div className="esd-drill-hdr-text">
                        <div className="legal-modal-title">{title}</div>
                        <div className="legal-modal-sub">{sub}</div>
                    </div>
                    <DetailModalControls onBack={breadcrumb?.length > 1 ? () => onJump(breadcrumb.length - 2) : undefined} onClose={onClose} />
                </div>
                {breadcrumb && breadcrumb.length > 1 && (
                    <div className="esd-breadcrumb">
                        {breadcrumb.map((crumb, i) => (
                            <span className="esd-breadcrumb-item" key={i}>
                                {i > 0 && <span className="esd-breadcrumb-sep">›</span>}
                                {i === breadcrumb.length - 1
                                    ? <span className="esd-breadcrumb-current">{crumb}</span>
                                    : <button type="button" className="esd-breadcrumb-link" onClick={() => onJump(i)}>{crumb}</button>}
                            </span>
                        ))}
                    </div>
                )}
                <div className="legal-modal-body esd-drill-body">{children}</div>
            </div>
        </div>
    );
}

function pctCell(v) {
    return v != null ? `${v.toFixed(1)}%` : '—';
}

// Small popup opened by clicking any card or table cell inside
// GlobalPctDeepDiveModal — shows just that one percentage plus the exact
// division used to compute it, so a user can verify a % of Revenue/ARR
// figure without having to find its formula annotation back on the page.
function PctFormulaModal({ label, value, formula, numerator, denominator, minuendPct, subtrahendPct, onClose }) {
    // Not every card/cell has a known numerator+denominator pair to show
    // (e.g. Prior Year cards when no comparison year is selected) — those
    // fall back to just the formula text, same as before this was added.
    // vs. AOP Variance cards/cells instead pass the two percentages plugged
    // into the subtraction (minuendPct − subtrahendPct), since that formula
    // has no single numerator/denominator to show.
    const hasValues = numerator != null && denominator;
    const hasDifference = minuendPct != null && subtrahendPct != null;
    return (
        <DrillModalShell icon="🧮" title={label} sub="Formula" onClose={onClose} maxWidth={440}>
            <div className="esd-kpi-grid" style={{ marginBottom: 0 }}>
                <div className="esd-kpi-card esd-kpi-card--b">
                    <div className="esd-kpi-label">{label}</div>
                    <div className="esd-kpi-value">{value}</div>
                    <div className="esd-kpi-sub">Formula: {formula}</div>
                    {hasValues && <div className="esd-kpi-sub">= {money(numerator)} ÷ {money(denominator)} × 100 = {value}</div>}
                    {hasDifference && <div className="esd-kpi-sub">= {minuendPct.toFixed(1)}% − {subtrahendPct.toFixed(1)}% = {value}</div>}
                </div>
            </div>
        </DrillModalShell>
    );
}

// Percentage cards (% of Revenue / % of ARR) open this instead of
// GlobalDeepDiveModal/GlobalRunRateModal — the same KpiCard-style tiles the
// page itself uses (not a dollar table) for the headline Actuals/AOP/PY/
// Variance (or Run Rate) % breakdown, plus a Monthly Detail table (Snapshot
// groups only — Run Rate is always annualized, no month-by-month trend to
// show) and a Department & Category Breakdown table, both expressed the
// same way: every figure is a percentage, never a dollar amount (same
// design as the 9 department dashboards' own PctDeepDiveModal).
function GlobalPctDeepDiveModal({ title, sub, cards, monthlyRows, categoryRows, categoryHeaders = ['Actuals', 'AOP', 'Prior Year', 'vs. AOP Variance'], metricLabel = 'Revenue', onClose }) {
    const [formulaDrill, setFormulaDrill] = useState(null);
    // categoryRows spanning more than one department (the company-wide KPI
    // cards' own breakdown) group into one row per department first —
    // clicking a department then drills into that department's own
    // categories. A categoryRows list already scoped to a single department
    // (e.g. clicking one department's row elsewhere first) has nothing to
    // group, so it skips straight to the flat category table as before.
    const [deptView, setDeptView] = useState(null);
    const departments = Array.from(new Set((categoryRows || []).map(r => r.department).filter(Boolean)));
    const isMultiDept = departments.length > 1;
    const deptRows = isMultiDept ? departments.map(dept => {
        const rows = categoryRows.filter(r => r.department === dept);
        const sumField = field => {
            const denom = rows.find(r => r._raw?.[field]?.denom)?._raw?.[field]?.denom;
            if (!denom) return { value: null, num: null, denom: null };
            const num = rows.reduce((acc, r) => acc + (r._raw?.[field]?.num || 0), 0);
            return { value: (num / denom) * 100, num, denom };
        };
        const actual = sumField('actual'); const aop = sumField('aop'); const py = sumField('py');
        // Point-difference of this department's own (already-aggregated)
        // Actual%/AOP% — not a separate raw $ sum — same reasoning as
        // categoryPctRows' own variance field, so a department's summed row
        // agrees with summing its individual categories' own variances.
        const variance = actual.value != null && aop.value != null ? actual.value - aop.value : null;
        return {
            label: shortDeptLabel(dept), department: dept,
            actual: actual.value, aop: aop.value, py: py.value, variance,
            _raw: { actual: { num: actual.num, denom: actual.denom }, aop: { num: aop.num, denom: aop.denom }, py: { num: py.num, denom: py.denom } },
        };
    }) : [];
    const activeCategoryRows = isMultiDept
        ? (deptView ? categoryRows.filter(r => r.department === deptView) : deptRows)
        : categoryRows;
    // Monthly Detail divides each month's own $ figure by that SAME month's
    // Revenue/ARR; Department & Category Breakdown divides each row's $
    // figure by the one scoped/company-wide Revenue/ARR total — two
    // different denominators, so each table gets its own formula text even
    // though both reuse the same 4 columns.
    const monthlyFormulas = {
        actual: `That month's Actuals+Forecast ÷ that month's ${metricLabel} × 100`,
        aop: `That month's AOP Budget ÷ that month's AOP ${metricLabel} × 100`,
        py: `That month's Prior Year Actuals ÷ that month's Prior Year ${metricLabel} × 100`,
        variance: `That month's Actuals+Forecast % of ${metricLabel} − that month's AOP % of ${metricLabel}`,
    };
    const categoryFormulas = {
        actual: `This row's Actuals+Forecast ÷ ${metricLabel} for the selected period × 100`,
        aop: `This row's AOP Budget ÷ AOP ${metricLabel} for the selected period × 100`,
        py: `This row's Prior Year Actuals ÷ Prior Year ${metricLabel} for the selected period × 100`,
        variance: `This row's Actuals+Forecast % of ${metricLabel} − this row's AOP % of ${metricLabel}`,
    };
    const columnLabel = { actual: categoryHeaders[0], aop: categoryHeaders[1], py: categoryHeaders[2], variance: categoryHeaders[3] };
    const openCell = (row, field, formulas) => setFormulaDrill({
        label: `${row.label} — ${columnLabel[field]}`,
        value: pctCell(row[field]),
        formula: formulas[field],
        numerator: row._raw?.[field]?.num,
        denominator: row._raw?.[field]?.denom,
        minuendPct: field === 'variance' ? row.actual : undefined,
        subtrahendPct: field === 'variance' ? row.aop : undefined,
    });

    return (
        <DrillModalShell icon="📊" title={title} sub={sub} onClose={onClose} maxWidth={960}>
            <div className="esd-kpi-grid" style={{ marginBottom: monthlyRows || categoryRows?.length ? 20 : 0 }}>
                {cards.map(c => (
                    <button
                        type="button" key={c.label}
                        className={`esd-kpi-card esd-kpi-card--${c.tone}`}
                        onClick={() => setFormulaDrill({ label: c.label, value: c.value, formula: c.formula, numerator: c.numerator, denominator: c.denominator, minuendPct: c.minuendPct, subtrahendPct: c.subtrahendPct })}
                    >
                        <div className="esd-kpi-top">
                            <span className="esd-kpi-icon">{c.icon}</span>
                            <span className="esd-kpi-hint">View formula →</span>
                        </div>
                        <div className="esd-kpi-label">{c.label}</div>
                        <div className="esd-kpi-value">{c.value}</div>
                        {c.sub && <div className="esd-kpi-sub">{c.sub}</div>}
                    </button>
                ))}
            </div>

            {monthlyRows && (
                <div className="esd-section">
                    <div className="esd-section-head">
                        <span className="esd-section-icon">🗓️</span>
                        <span className="esd-section-title">Monthly Detail</span>
                        <span className="esd-section-note">Click a figure for its formula</span>
                    </div>
                    <div className="esd-table-wrap">
                        <table className="esd-table esd-table-cellclick">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Month</th>
                                    <th>Actuals</th>
                                    <th>AOP</th>
                                    <th>Prior Year</th>
                                    <th>vs. AOP Variance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyRows.map(row => (
                                    <tr key={row.label}>
                                        <td style={{ textAlign: 'left' }}>{row.label}</td>
                                        <td onClick={() => openCell(row, 'actual', monthlyFormulas)}><strong>{pctCell(row.actual)}</strong></td>
                                        <td onClick={() => openCell(row, 'aop', monthlyFormulas)}><strong>{pctCell(row.aop)}</strong></td>
                                        <td onClick={() => openCell(row, 'py', monthlyFormulas)}><strong>{pctCell(row.py)}</strong></td>
                                        <td onClick={() => openCell(row, 'variance', monthlyFormulas)}><strong>{pctCell(row.variance)}</strong></td>
                                    </tr>
                                ))}
                                {!monthlyRows.length && (
                                    <tr><td colSpan={5} className="esd-empty-row">No monthly data in this scope.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!!activeCategoryRows?.length && (
                <div className="esd-section">
                    <div className="esd-section-head">
                        <span className="esd-section-icon">🏢</span>
                        <span className="esd-section-title">
                            {isMultiDept && deptView ? `${shortDeptLabel(deptView)} — Category Breakdown` : isMultiDept ? 'Department Breakdown' : 'Department & Category Breakdown'}
                        </span>
                        <span className="esd-section-note">
                            {isMultiDept && !deptView ? 'Click a department for its category breakdown' : 'Click a figure for its formula'}
                        </span>
                        {isMultiDept && deptView && (
                            <button type="button" className="esd-breadcrumb-link" style={{ marginLeft: 'auto' }} onClick={() => setDeptView(null)}>← All Departments</button>
                        )}
                    </div>
                    <div className="esd-table-wrap">
                        <table className="esd-table esd-table-cellclick">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>{isMultiDept && !deptView ? 'Department' : 'Department — Category'}</th>
                                    <th>{categoryHeaders[0]}</th>
                                    <th>{categoryHeaders[1]}</th>
                                    <th>{categoryHeaders[2]}</th>
                                    <th>{categoryHeaders[3]}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isMultiDept && !deptView ? activeCategoryRows.map(row => (
                                    <tr key={row.label} onClick={() => setDeptView(row.department)} style={{ cursor: 'pointer' }}>
                                        <td style={{ textAlign: 'left' }}>{row.label}</td>
                                        <td><strong>{pctCell(row.actual)}</strong></td>
                                        <td><strong>{pctCell(row.aop)}</strong></td>
                                        <td><strong>{pctCell(row.py)}</strong></td>
                                        <td><strong>{pctCell(row.variance)}</strong></td>
                                    </tr>
                                )) : activeCategoryRows.map(row => (
                                    <tr key={row.label}>
                                        <td style={{ textAlign: 'left' }}>{row.label}</td>
                                        <td onClick={() => openCell(row, 'actual', categoryFormulas)}><strong>{pctCell(row.actual)}</strong></td>
                                        <td onClick={() => openCell(row, 'aop', categoryFormulas)}><strong>{pctCell(row.aop)}</strong></td>
                                        <td onClick={() => openCell(row, 'py', categoryFormulas)}><strong>{pctCell(row.py)}</strong></td>
                                        <td onClick={() => openCell(row, 'variance', categoryFormulas)}><strong>{pctCell(row.variance)}</strong></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {formulaDrill && <PctFormulaModal {...formulaDrill} onClose={() => setFormulaDrill(null)} />}
        </DrillModalShell>
    );
}

function VarianceStack({ actual, base }) {
    const diff = actual - base;
    const favorable = diff <= 0;
    return (
        <span style={{ color: favorable ? '#15803d' : '#dc2626' }}>
            <strong>{money(diff)}</strong><br /><small>{variancePct(actual, base)}</small>
        </span>
    );
}

// Sums every category row belonging to one department (or every department,
// when `department` is null) into one {month: {actual,aop,py}} map per
// scope, restricted to `months`.
function sumCategoryRows(byCategory, months, department) {
    const monthSet = new Set(months);
    const totals = Object.fromEntries(months.map(m => [m, { actual: 0, aop: 0, py: 0 }]));
    Object.values(byCategory).forEach(row => {
        if (department && row.department !== department) return;
        (row.monthly || []).forEach(m => {
            if (!monthSet.has(m.month)) return;
            totals[m.month].actual += m.actual || 0;
            totals[m.month].aop += m.aop || 0;
            totals[m.month].py += m.py || 0;
        });
    });
    return totals;
}

function sumTotals(monthMap) {
    return Object.values(monthMap).reduce((s, v) => ({ actual: s.actual + v.actual, aop: s.aop + v.aop, py: s.py + v.py }), { actual: 0, aop: 0, py: 0 });
}

// Department-level breakdown of whichever months are in scope — grouped
// from global_total.by_category's "{Department} — {Category}" rows since
// Global Dashboard has no per-department monthly row of its own stored
// server-side (only the combined Total and each department's own
// categories).
function departmentBreakdownForScope(byCategory, months) {
    const byDept = {};
    Object.values(byCategory).forEach(row => {
        if (!byDept[row.department]) byDept[row.department] = { actual: 0, aop: 0, py: 0 };
        const monthSet = new Set(months);
        (row.monthly || []).forEach(m => {
            if (!monthSet.has(m.month)) return;
            byDept[row.department].actual += m.actual || 0;
            byDept[row.department].aop += m.aop || 0;
            byDept[row.department].py += m.py || 0;
        });
    });
    // Department order follows byCategory's own insertion order (every
    // consumer of this list shows departments in that same fixed sequence
    // now, not sorted by name or by magnitude), which in turn follows
    // DEPARTMENTS in global_views/dashboard.py. A department with nothing
    // in Actual/AOP/PY for the selected scope is dropped — just noise, same
    // as every other category/department table across the app.
    return Object.entries(byDept)
        .map(([department, totals]) => ({ department, ...totals }))
        .filter(row => row.actual || row.aop || row.py);
}

// Category breakdown for one department's own months in scope.
function categoryBreakdownForScope(byCategory, months, department) {
    const monthSet = new Set(months);
    return Object.values(byCategory)
        .filter(row => row.department === department)
        .map(row => {
            const totals = (row.monthly || []).reduce((s, m) => monthSet.has(m.month)
                ? { actual: s.actual + (m.actual || 0), aop: s.aop + (m.aop || 0), py: s.py + (m.py || 0) }
                : s, { actual: 0, aop: 0, py: 0 });
            return { category: row.category, ...totals };
        })
        // A category with nothing in Actual/AOP/PY for the selected scope is
        // dropped — just noise, same as every other category/department
        // table across the app. (Categories, unlike departments, have no
        // fixed display order of their own — still sorted by magnitude.)
        .filter(row => row.actual || row.aop || row.py)
        .sort((a, b) => b.actual - a.actual);
}

// Deepest level's own monthly series — one specific department+category row,
// not summed with any sibling (there's only one row behind this key), still
// restricted to whichever months are in scope.
function categoryMonthlyForScope(byCategory, months, department, category) {
    const monthSet = new Set(months);
    const row = Object.values(byCategory).find(r => r.department === department && r.category === category);
    const totals = Object.fromEntries(months.map(m => [m, { actual: 0, aop: 0, py: 0 }]));
    (row?.monthly || []).forEach(m => {
        if (!monthSet.has(m.month)) return;
        totals[m.month] = { actual: m.actual || 0, aop: m.aop || 0, py: m.py || 0 };
    });
    return totals;
}

// Deep-dive modal: a monthly table for the clicked scope (every month in
// scope — the whole company, one department's own months, or — deepest —
// one single department+category's own months), plus a breakdown table one
// level down (every department, that department's own categories, or
// nothing once a single category is reached — there's no level below a
// single category's own numbers). Matches the 9 department dashboards' own
// DeepDiveModal in spirit — monthly detail plus a breakdown underneath —
// but Global's breakdown dimension is "department" then "category" instead
// of a single department's own category list.
function GlobalDeepDiveModal({ title, sub, months, department, category, data, breadcrumb, onJump, onClose, onDrillDepartment, onDrillCategory, onDrillMonth, onDrillSplit, onDrillTotal }) {
    const byCategory = data.global_total.by_category;
    const monthMap = category ? categoryMonthlyForScope(byCategory, months, department, category)
        : department ? sumCategoryRows(byCategory, months, department)
        : null;
    const totalMonthly = (data.global_total.total.monthly || []).filter(m => months.includes(m.month));
    const monthlyRows = monthMap
        ? totalMonthly.map(m => ({ month: m.month, label: m.label, ...monthMap[m.month] }))
        : totalMonthly;
    const totals = sumTotals(monthMap || Object.fromEntries(totalMonthly.map(m => [m.month, m])));

    const breakdownRows = category ? []
        : department ? categoryBreakdownForScope(byCategory, months, department)
        : departmentBreakdownForScope(byCategory, months);
    const handleRowClick = row => (department ? onDrillCategory?.(row.category) : onDrillDepartment?.(row.department));
    // Once a specific department is known (level 1 — one department, every
    // category; or level 2 — one department, one category), the records
    // behind it are reachable (there's exactly one department's own model
    // to query), so both the KPI tiles/Period Total (the whole scope) and
    // each month (just that month) can open a Headcount vs. Non-Headcount
    // split, same as every department's own dashboard. Company-wide
    // (department null) has no single model to fetch from, so it keeps its
    // original "click a month to see the department breakdown as of that
    // month" behavior instead.
    const canSplit = !!department && !!onDrillSplit;
    // Company-wide (department null) has no single department's Headcount/
    // Non-Headcount split to offer, but it does have EVERY department's own
    // HC/Non-HC split — onDrillTotal opens GlobalDeptHcNonHcModal, which
    // groups every department's HC total and Non-HC total into two
    // sections, scoped to whichever row was clicked (a single month, or the
    // whole FY via a KPI tile / Period Total) — same depth and click
    // convention as canSplit's department-scoped HC/Non-HC split, just one
    // dimension wider.
    const canDrillTotal = !department && !!onDrillTotal;
    const monthClickable = canSplit || canDrillTotal || (!!onDrillMonth && monthlyRows.length > 1);
    const handleMonthClick = (month, label) => (
        canSplit ? onDrillSplit?.([month], `${title} — ${label}`)
            : canDrillTotal ? onDrillTotal?.([month], `${title} — ${label}`)
                : onDrillMonth?.(month, label)
    );
    const monthClickHint = (canSplit || canDrillTotal) ? 'Click a total for the HC / Non-HC split'
        : `Click a month for its own ${department ? 'category' : 'department'} breakdown`;
    const kpiClick = canSplit ? () => onDrillSplit(months, `${title} — FY Total`)
        : canDrillTotal ? () => onDrillTotal(months, `${title} — FY Total`)
            : null;
    const KpiTag = kpiClick ? 'button' : 'div';
    const kpiProps = kpiClick ? { type: 'button', onClick: kpiClick } : {};
    const kpiHint = (canSplit || canDrillTotal) ? 'HC / Non-HC split →' : null;

    return (
        <DrillModalShell icon="🔎" title={title} sub={sub} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}>
            <div className="esd-kpi-grid" style={{ marginBottom: 20 }}>
                <KpiTag {...kpiProps} className="esd-kpi-card esd-kpi-card--a">
                    {kpiHint && <div className="esd-kpi-top"><span className="esd-kpi-hint">{kpiHint}</span></div>}
                    <div className="esd-kpi-label">Actual/Forecast</div>
                    <div className="esd-kpi-value">{money(totals.actual)}</div>
                </KpiTag>
                <KpiTag {...kpiProps} className="esd-kpi-card esd-kpi-card--b">
                    {kpiHint && <div className="esd-kpi-top"><span className="esd-kpi-hint">{kpiHint}</span></div>}
                    <div className="esd-kpi-label">AOP</div>
                    <div className="esd-kpi-value">{money(totals.aop)}</div>
                </KpiTag>
                <KpiTag {...kpiProps} className="esd-kpi-card esd-kpi-card--c">
                    {kpiHint && <div className="esd-kpi-top"><span className="esd-kpi-hint">{kpiHint}</span></div>}
                    <div className="esd-kpi-label">Prior Year</div>
                    <div className="esd-kpi-value">{money(totals.py)}</div>
                </KpiTag>
                <KpiTag {...kpiProps} className={`esd-kpi-card esd-kpi-card--${totals.actual - totals.aop <= 0 ? 'good' : 'bad'}`}>
                    {kpiHint && <div className="esd-kpi-top"><span className="esd-kpi-hint">{kpiHint}</span></div>}
                    <div className="esd-kpi-label">Var. to AOP</div>
                    <div className={`esd-kpi-value tone-${totals.actual - totals.aop <= 0 ? 'good' : 'bad'}`}>{money(totals.actual - totals.aop)}</div>
                    <div className={`esd-kpi-sub tone-${totals.actual - totals.aop <= 0 ? 'good' : 'bad'}`}>{variancePct(totals.actual, totals.aop)}</div>
                </KpiTag>
                <KpiTag {...kpiProps} className={`esd-kpi-card esd-kpi-card--${totals.actual - totals.py <= 0 ? 'good' : 'bad'}`}>
                    {kpiHint && <div className="esd-kpi-top"><span className="esd-kpi-hint">{kpiHint}</span></div>}
                    <div className="esd-kpi-label">Var. to PY</div>
                    <div className={`esd-kpi-value tone-${totals.actual - totals.py <= 0 ? 'good' : 'bad'}`}>{money(totals.actual - totals.py)}</div>
                    <div className={`esd-kpi-sub tone-${totals.actual - totals.py <= 0 ? 'good' : 'bad'}`}>{variancePct(totals.actual, totals.py)}</div>
                </KpiTag>
            </div>

            <div className="esd-section">
                <div className="esd-section-head">
                    <span className="esd-section-icon">🗓️</span>
                    <span className="esd-section-title">Monthly Detail{department ? ` — ${shortDeptLabel(department)}${category ? ` — ${category}` : ''}` : ''}</span>
                    <span className="esd-section-note">{monthClickable ? monthClickHint : ''}</span>
                </div>
                <div className="esd-table-wrap">
                    <table className={`esd-table${monthClickable ? ' esd-table-cellclick' : ''}`}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Month</th>
                                <th>Actual/Forecast</th>
                                <th>AOP</th>
                                <th>Prior Year</th>
                                <th>Var. to AOP</th>
                                <th>Var. to PY</th>
                            </tr>
                        </thead>
                        <tbody>
                            {monthlyRows.map(row => {
                                const clickTitle = monthClickable
                                    ? ((canSplit || canDrillTotal) ? `Headcount / Non-Headcount split — ${row.label}` : `${department ? 'Category' : 'Department'} breakdown — ${row.label}`)
                                    : undefined;
                                const cellProps = {
                                    className: monthClickable ? 'esd-cell-click' : undefined,
                                    onClick: monthClickable ? () => handleMonthClick(row.month, row.label) : undefined,
                                    title: clickTitle,
                                };
                                return (
                                    <tr key={row.month}>
                                        <td {...cellProps} style={{ textAlign: 'left' }}>{row.label}</td>
                                        <td {...cellProps}><strong>{money(row.actual)}</strong></td>
                                        <td {...cellProps}><strong>{money(row.aop)}</strong></td>
                                        <td {...cellProps}><strong>{money(row.py)}</strong></td>
                                        <td {...cellProps}><VarianceStack actual={row.actual} base={row.aop} /></td>
                                        <td {...cellProps}><VarianceStack actual={row.actual} base={row.py} /></td>
                                    </tr>
                                );
                            })}
                            {!monthlyRows.length && (
                                <tr><td colSpan={6} className="esd-empty-row">No monthly data in this scope.</td></tr>
                            )}
                        </tbody>
                        {!!monthlyRows.length && (() => {
                            const totalCellProps = {
                                className: (canSplit || canDrillTotal) ? 'esd-cell-click' : undefined,
                                onClick: canSplit ? () => onDrillSplit(months, `${title} — FY Total`) : canDrillTotal ? () => onDrillTotal(months, `${title} — FY Total`) : undefined,
                                title: (canSplit || canDrillTotal) ? 'Headcount / Non-Headcount split — FY Total' : undefined,
                            };
                            return (
                                <tfoot>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}><strong>Period Total</strong></td>
                                        <td {...totalCellProps}><strong>{money(totals.actual)}</strong></td>
                                        <td {...totalCellProps}><strong>{money(totals.aop)}</strong></td>
                                        <td {...totalCellProps}><strong>{money(totals.py)}</strong></td>
                                        <td {...totalCellProps}><VarianceStack actual={totals.actual} base={totals.aop} /></td>
                                        <td {...totalCellProps}><VarianceStack actual={totals.actual} base={totals.py} /></td>
                                    </tr>
                                </tfoot>
                            );
                        })()}
                    </table>
                </div>
            </div>

            {!category && (
                <div className="esd-section">
                    <div className="esd-section-head">
                        <span className="esd-section-icon">{department ? '🏷️' : '🏢'}</span>
                        <span className="esd-section-title">{department ? 'Category Breakdown' : 'Department Breakdown'}</span>
                        <span className="esd-section-note">{department ? 'Click a category for its own monthly detail' : 'Click a department for its own category breakdown'}</span>
                    </div>
                    <div className="esd-table-wrap">
                        <table className="esd-table esd-table-cellclick">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>{department ? 'Category' : 'Department'}</th>
                                    <th>Actual/Forecast</th>
                                    <th>AOP</th>
                                    <th>Prior Year</th>
                                    <th>Var. to AOP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {breakdownRows.map(row => (
                                    <tr
                                        key={row.department || row.category}
                                        className="esd-row-drillable"
                                        onClick={() => handleRowClick(row)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td style={{ textAlign: 'left' }}>{row.department ? shortDeptLabel(row.department) : row.category}</td>
                                        <td>{money(row.actual)}</td>
                                        <td>{money(row.aop)}</td>
                                        <td>{money(row.py)}</td>
                                        <td><VarianceStack actual={row.actual} base={row.aop} /></td>
                                    </tr>
                                ))}
                                {!breakdownRows.length && (
                                    <tr><td colSpan={5} className="esd-empty-row">No data in this scope.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </DrillModalShell>
    );
}

// Cosmetic only — every real source key (hc, nonhc, commission, and
// whatever gets added later) still comes from the data itself (see
// sourceKeysOf below), never from this list. A key not listed here still
// shows up correctly, just capitalized instead of specially worded.
const SOURCE_LABELS = { hc: 'Headcount', nonhc: 'Non-Headcount', commission: 'Commission' };
const SOURCE_SUBLABELS = {
    hc: 'At employee level', nonhc: 'At vendor / expense category level', commission: 'At rep level',
};
const titleCase = s => s.charAt(0).toUpperCase() + s.slice(1);
const lowerFirst = s => s.charAt(0).toLowerCase() + s.slice(1);
const sourceLabel = key => SOURCE_LABELS[key] || titleCase(key);
const sourceSubLabel = key => SOURCE_SUBLABELS[key] || '';

// A category row carries one nested sub-row per cost source it has — always
// hc/nonhc, plus commission only where global_views/summary.py's own
// `cfg.get("commission")` guard added it (the 3 Sales dashboards). Reading
// whichever of those sub-rows actually exist on the row — rather than
// hardcoding "hc, nonhc, commission" as the fixed list — is what makes this
// correct if a 4th source is ever added: a sub-row is just any nested object
// with its own `.monthly` series, whatever its key is.
function sourceKeysOf(row) {
    return Object.keys(row).filter(k => row[k] && typeof row[k] === 'object' && !Array.isArray(row[k]) && Array.isArray(row[k].monthly));
}

// Shared by GlobalDeptHcNonHcModal and GlobalSourceByDeptModal — sums every
// category row's own cost-source sub-rows (whichever ones that row actually
// has — see sourceKeysOf), grouped by department, for whichever `months`
// the caller is scoped to.
function sumBySourceAndDepartment(byCategory, months) {
    const monthSet = new Set(months);
    const zero = () => ({ actual: 0, aop: 0, py: 0 });
    const add = (base, m) => { base.actual += m.actual || 0; base.aop += m.aop || 0; base.py += m.py || 0; };
    const deptMap = {};
    Object.values(byCategory).forEach(row => {
        const dept = row.department;
        if (!deptMap[dept]) deptMap[dept] = {};
        sourceKeysOf(row).forEach(key => {
            if (!deptMap[dept][key]) deptMap[dept][key] = zero();
            row[key].monthly.forEach(m => { if (monthSet.has(m.month)) add(deptMap[dept][key], m); });
        });
    });
    return deptMap;
}

// Company-wide (no department filter) Headcount/Non-Headcount split — what
// a KPI tile, a Monthly Detail row, or Period Total in the top-level
// GlobalDeepDiveModal opens, same "click a dollar figure for its HC / Non-HC
// split" convention every department's own dashboard already has, and the
// SAME two-row shape as that department-scoped GlobalMonthSplitModal (just
// Headcount/Non-Headcount totals, not every department expanded at once) —
// each row drills one level further into GlobalSourceByDeptModal, that
// source's own per-department breakdown, same depth every department's own
// MonthSplitModal → records already has.
function GlobalDeptHcNonHcModal({ title, sub, months, data, breadcrumb, onJump, onClose, onDrillSource }) {
    const deptMap = sumBySourceAndDepartment(data.global_total.by_category, months);
    const depts = Object.keys(deptMap);
    // Every source key found on ANY department's own row — a source that
    // only exists for some departments (Commission, today) still shows up
    // here once, summed across whichever departments actually have it; a
    // source with zero everywhere simply sums to zero and is dropped below.
    const sourceKeys = Array.from(new Set(depts.flatMap(d => Object.keys(deptMap[d]))));
    const sumRows = key => depts.reduce((acc, d) => {
        const r = deptMap[d][key];
        if (!r) return acc;
        return { actual: acc.actual + r.actual, aop: acc.aop + r.aop, py: acc.py + r.py };
    }, { actual: 0, aop: 0, py: 0 });
    const rows = sourceKeys
        .map(key => ({ source: key, label: sourceLabel(key), sub: `Every department, ${lowerFirst(sourceSubLabel(key))}`, ...sumRows(key) }))
        .filter(r => r.actual || r.aop || r.py);
    const grandTotal = rows.reduce((acc, r) => ({ actual: acc.actual + r.actual, aop: acc.aop + r.aop, py: acc.py + r.py }), { actual: 0, aop: 0, py: 0 });

    return (
        <DrillModalShell icon="🔎" title={title} sub={sub} maxWidth={820} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}>
            <div className="esd-table-wrap">
                <table className="esd-table esd-table-cellclick">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Source</th>
                            <th>Actual/Forecast</th>
                            <th>AOP</th>
                            <th>Prior Year</th>
                            <th>Var. to AOP</th>
                            <th>Var. to PY</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.source} className="esd-row-drillable" onClick={() => onDrillSource(r.source, r.label)} style={{ cursor: 'pointer' }}>
                                <td style={{ textAlign: 'left' }}>
                                    <div>{r.label}</div>
                                    <div style={{ fontSize: '0.82em', fontWeight: 500, color: 'var(--esd-muted, #65676b)' }}>{r.sub}</div>
                                </td>
                                <td><strong>{money(r.actual)}</strong></td>
                                <td><strong>{money(r.aop)}</strong></td>
                                <td><strong>{money(r.py)}</strong></td>
                                <td><VarianceStack actual={r.actual} base={r.aop} /></td>
                                <td><VarianceStack actual={r.actual} base={r.py} /></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="esd-row-drillable" onClick={() => onDrillSource(null, 'Total')} style={{ cursor: 'pointer' }}>
                            <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                            <td><strong>{money(grandTotal.actual)}</strong></td>
                            <td><strong>{money(grandTotal.aop)}</strong></td>
                            <td><strong>{money(grandTotal.py)}</strong></td>
                            <td><VarianceStack actual={grandTotal.actual} base={grandTotal.aop} /></td>
                            <td><VarianceStack actual={grandTotal.actual} base={grandTotal.py} /></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </DrillModalShell>
    );
}

// One level deeper than GlobalDeptHcNonHcModal — clicking its Headcount or
// Non-Headcount row lands here: that ONE source's own total, broken out one
// row per department. Clicking its Total row instead (source === null)
// shows every source combined, broken out the same way. Clicking a
// department pushes the same DeepDiveModal layer a Department Breakdown row
// already opens.
function GlobalSourceByDeptModal({ title, sub, source, months, data, breadcrumb, onJump, onClose, onDrillDepartment }) {
    const deptMap = sumBySourceAndDepartment(data.global_total.by_category, months);
    const valueFor = d => source
        ? deptMap[d][source]
        : Object.values(deptMap[d]).reduce((acc, r) => ({ actual: acc.actual + r.actual, aop: acc.aop + r.aop, py: acc.py + r.py }), { actual: 0, aop: 0, py: 0 });
    // Not every department has every source (Commission only exists for the
    // 3 Sales dashboards) — deptMap[d] simply has no entry for it there, so
    // this is the same "that department contributes nothing" skip every
    // other source-aware view in this file already does. No explicit sort —
    // deptMap's own insertion order already follows DEPARTMENTS in
    // global_views/dashboard.py, the one fixed sequence every department
    // list across the app now shows.
    const depts = Object.keys(deptMap)
        .filter(d => (source ? deptMap[d][source] : true) && (valueFor(d).actual || valueFor(d).aop || valueFor(d).py));
    const total = depts.reduce((acc, d) => ({
        actual: acc.actual + valueFor(d).actual, aop: acc.aop + valueFor(d).aop, py: acc.py + valueFor(d).py,
    }), { actual: 0, aop: 0, py: 0 });

    return (
        <DrillModalShell icon="🏢" title={title} sub={sub} maxWidth={820} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}>
            <div className="esd-table-wrap">
                <table className="esd-table esd-table-cellclick">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Department</th>
                            <th>Actual/Forecast</th>
                            <th>AOP</th>
                            <th>Prior Year</th>
                            <th>Var. to AOP</th>
                            <th>Var. to PY</th>
                        </tr>
                    </thead>
                    <tbody>
                        {depts.map(d => (
                            <tr key={d} className="esd-row-drillable" onClick={() => onDrillDepartment(d)} style={{ cursor: 'pointer' }}>
                                <td style={{ textAlign: 'left' }}>{shortDeptLabel(d)}</td>
                                <td>{money(valueFor(d).actual)}</td>
                                <td>{money(valueFor(d).aop)}</td>
                                <td>{money(valueFor(d).py)}</td>
                                <td><VarianceStack actual={valueFor(d).actual} base={valueFor(d).aop} /></td>
                                <td><VarianceStack actual={valueFor(d).actual} base={valueFor(d).py} /></td>
                            </tr>
                        ))}
                        {!depts.length && (
                            <tr><td colSpan={6} className="esd-empty-row">No data in this scope.</td></tr>
                        )}
                    </tbody>
                    {!!depts.length && (
                        <tfoot>
                            <tr>
                                <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                <td><strong>{money(total.actual)}</strong></td>
                                <td><strong>{money(total.aop)}</strong></td>
                                <td><strong>{money(total.py)}</strong></td>
                                <td><VarianceStack actual={total.actual} base={total.aop} /></td>
                                <td><VarianceStack actual={total.actual} base={total.py} /></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </DrillModalShell>
    );
}

// Deepest-but-one level — a single department+category's one month, split
// into Headcount vs. Non-Headcount (the `row.hc`/`row.nonhc` sub-rows
// global_summary.py now attaches to each category), same shape and figures
// as every department's own MonthSplitModal — `months` is whichever scope
// was clicked (a single month's row, or the whole FY Total from a KPI tile
// or Period Total), and `category` may be null (summed across every one of
// `department`'s own categories, reached from the level-1 "department, all
// categories" view) or one specific category (reached from a single
// category's own Monthly Detail). Each row drills one level further, into
// that source's own records for this same scope.
function GlobalMonthSplitModal({ title, sub, department, category, months, data, breadcrumb, onJump, onClose, onDrillSource, onDrillCategories }) {
    const byCategory = data.global_total.by_category;
    const monthSet = new Set(months);
    const zero = () => ({ actual: 0, aop: 0, py: 0 });
    const sums = {};
    Object.values(byCategory).forEach(row => {
        if (row.department !== department) return;
        if (category && row.category !== category) return;
        sourceKeysOf(row).forEach(key => {
            if (!sums[key]) sums[key] = zero();
            row[key].monthly.forEach(m => { if (monthSet.has(m.month)) { sums[key].actual += m.actual || 0; sums[key].aop += m.aop || 0; sums[key].py += m.py || 0; } });
        });
    });
    // Whichever sources this department's own rows actually carry — always
    // hc/nonhc, plus commission only for the 3 Sales dashboards (omitted
    // entirely rather than shown as an always-zero row for everyone else).
    const rows = Object.keys(sums)
        .map(key => ({ source: key, label: sourceLabel(key), subLabel: sourceSubLabel(key), ...sums[key] }))
        .filter(r => r.actual || r.aop || r.py);
    const total = rows.reduce((s, r) => ({ actual: s.actual + r.actual, aop: s.aop + r.aop, py: s.py + r.py }), { actual: 0, aop: 0, py: 0 });
    // This department's own combined total, broken out by category —
    // same destination the parent DeepDiveModal's own inline Category
    // Breakdown table already offers, surfaced here too as its own row,
    // generic over every department — skipped once already scoped to one
    // specific category, since there's nothing finer to break it into at
    // that point.
    const showCategories = !category && !!onDrillCategories;

    return (
        <DrillModalShell icon="🔎" title={title} sub={sub} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose} maxWidth={820}>
            <div className="esd-table-wrap">
                <table className="esd-table esd-table-cellclick">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Source</th>
                            <th>Actual/Forecast</th>
                            <th>AOP</th>
                            <th>Prior Year</th>
                            <th>Var AOP and %</th>
                            <th>Var PY and %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.source} onClick={() => onDrillSource(r.source)}>
                                <td style={{ textAlign: 'left' }}>
                                    <div>{r.label}</div>
                                    <div style={{ fontSize: '0.82em', fontWeight: 500, color: '#65676b' }}>{r.subLabel}</div>
                                </td>
                                <td><strong>{money(r.actual)}</strong></td>
                                <td><strong>{money(r.aop)}</strong></td>
                                <td><strong>{money(r.py)}</strong></td>
                                <td><VarianceStack actual={r.actual} base={r.aop} /></td>
                                <td><VarianceStack actual={r.actual} base={r.py} /></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        {/* The old showCategories row here duplicated this Total row
                            number-for-number (both are this department's own
                            combined total) — merged into one clickable Total row
                            instead of showing the same figures twice. */}
                        <tr onClick={showCategories ? onDrillCategories : undefined} style={showCategories ? { cursor: 'pointer' } : undefined}>
                            <td style={{ textAlign: 'left' }}>
                                <div><strong>Total</strong></div>
                                {showCategories && <div style={{ fontSize: '0.82em', fontWeight: 500, color: '#65676b' }}>By category — combined {rows.map(r => r.label).join(' + ')}</div>}
                            </td>
                            <td><strong>{money(total.actual)}</strong></td>
                            <td><strong>{money(total.aop)}</strong></td>
                            <td><strong>{money(total.py)}</strong></td>
                            <td><VarianceStack actual={total.actual} base={total.aop} /></td>
                            <td><VarianceStack actual={total.actual} base={total.py} /></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </DrillModalShell>
    );
}

// One level deeper than GlobalMonthSplitModal's own department-combined row
// — that department's own categories, each the SAME combined-source figure
// (hc+nonhc+commission, whichever this department has) the row above it
// shows, just split out, generic over every department. Clicking a
// category goes straight to that category's own combined records, same
// one-hop-to-records depth every other row in this drill stack already has.
function GlobalCategoryBreakdownModal({ title, sub, department, months, data, breadcrumb, onJump, onClose, onDrillCategory }) {
    const categoryRows = categoryBreakdownForScope(data.global_total.by_category, months, department);
    const total = categoryRows.reduce((acc, r) => ({ actual: acc.actual + r.actual, aop: acc.aop + r.aop, py: acc.py + r.py }), { actual: 0, aop: 0, py: 0 });

    return (
        <DrillModalShell icon="🏷️" title={title} sub={sub} maxWidth={760} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}>
            <div className="esd-table-wrap">
                <table className="esd-table esd-table-cellclick">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Category</th>
                            <th>Actual/Forecast</th>
                            <th>AOP</th>
                            <th>Prior Year</th>
                            <th>Var. to AOP</th>
                            <th>Var. to PY</th>
                        </tr>
                    </thead>
                    <tbody>
                        {categoryRows.map(row => (
                            <tr key={row.category} className="esd-row-drillable" onClick={() => onDrillCategory(row.category)} style={{ cursor: 'pointer' }}>
                                <td style={{ textAlign: 'left' }}>{row.category}</td>
                                <td><strong>{money(row.actual)}</strong></td>
                                <td><strong>{money(row.aop)}</strong></td>
                                <td><strong>{money(row.py)}</strong></td>
                                <td><VarianceStack actual={row.actual} base={row.aop} /></td>
                                <td><VarianceStack actual={row.actual} base={row.py} /></td>
                            </tr>
                        ))}
                        {!categoryRows.length && (
                            <tr><td colSpan={6} className="esd-empty-row">No category data in this scope.</td></tr>
                        )}
                    </tbody>
                    {!!categoryRows.length && (
                        <tfoot>
                            <tr>
                                <td style={{ textAlign: 'left' }}><strong>{shortDeptLabel(department)} Total</strong></td>
                                <td><strong>{money(total.actual)}</strong></td>
                                <td><strong>{money(total.aop)}</strong></td>
                                <td><strong>{money(total.py)}</strong></td>
                                <td><VarianceStack actual={total.actual} base={total.aop} /></td>
                                <td><VarianceStack actual={total.actual} base={total.py} /></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </DrillModalShell>
    );
}

// Deepest level — the actual employee/vendor records behind one
// department+category+month+source. Global has no record model of its own
// (every department's HC/Non-HC records come from a different Django
// model), so this fetches from /api/global/raw/ (which looks up the right
// model per department) instead of a prefetched prop, unlike
// GlobalDeepDiveModal/GlobalMonthSplitModal which only ever read off the
// already-loaded `data` prop. source === 'total' (the HC & Non-HC 360 tab's
// own Grand Total row) fetches both and tags each with `_source` so
// GlobalRecordDetails can tell them apart in one combined list, same as
// every department's own HC & Non-HC 360 tab.
function GlobalRecordsModal({ title, sub, department, source, category, months, currentYear, priorYear, breadcrumb, onJump, onClose }) {
    const [records, setRecords] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showItems, setShowItems] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true); setError('');
        const fetchSource = src => fetch(`${API_URL}/api/global/raw/?department=${encodeURIComponent(department)}&source=${src}`, { credentials: 'include' })
            .then(res => readApiJson(res, 'Could not load records.'))
            .then(body => (body.records || []).map(r => ({ ...r, _source: src })));
        // Commission only exists for the Sales APAC/NAM/Solution departments
        // — fetching it for any other department is harmless (global_raw
        // returns {has_data: false, records: []} rather than an error), so
        // it's always included in the 'total' merge instead of needing to
        // know in advance which departments have it.
        const sources = source === 'total' ? ['hc', 'nonhc', 'commission'] : [source];
        Promise.all(sources.map(fetchSource))
            .then(lists => { if (!cancelled) setRecords(lists.flat()); })
            .catch(err => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [department, source]);

    return (
        <DrillModalShell icon={source === 'hc' ? '👥' : source === 'nonhc' ? '📦' : source === 'commission' ? '💰' : '🧾'} title={title} sub={sub} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose} maxWidth={1100}>
            <GlobalRecordDetails
                source={source} category={category} records={records} months={months}
                currentYear={currentYear} priorYear={priorYear} loading={loading} error={error}
                selectedCategory={selectedCategory} onSelectCategory={setSelectedCategory}
                showItems={showItems} onShowItems={setShowItems}
            />
        </DrillModalShell>
    );
}

// This department's own categories' Run Rate rows — by_category already
// carries each row's own annualized run_rate (combine_rates over that
// category's own records), same figures the dollar tables' Run Rate
// sections are built from, just grouped one level deeper here.
function categoryRunRateForDepartment(byCategory, department) {
    return Object.values(byCategory)
        .filter(row => row.department === department && row.run_rate)
        .map(row => ({ category: row.category, run_rate: row.run_rate }))
        .sort((a, b) => (b.run_rate?.current || 0) - (a.run_rate?.current || 0));
}

// Run Rate's own click target — annualized, not scoped by the page filters
// (same as every department dashboard's Run Rate section), broken down by
// department first, then (one level deeper) that department's own
// categories — Global has no HC/Non-HC split, so "category" stands in for
// that dimension here, same as GlobalDeepDiveModal's dollar drill-down.
function GlobalRunRateModal({ label, sub, data, department, breadcrumb, onJump, onClose, onDrillDepartment }) {
    const departments = data.department_breakdown || [];
    const total = departments.reduce((s, d) => ({
        current: s.current + (d.run_rate?.current || 0),
        dec_aop: s.dec_aop + (d.run_rate?.dec_aop || 0),
        dec_fcst: s.dec_fcst + (d.run_rate?.dec_fcst || 0),
    }), { current: 0, dec_aop: 0, dec_fcst: 0 });

    const categoryRows = department ? categoryRunRateForDepartment(data.global_total.by_category, department) : [];
    const rows = department ? categoryRows : departments;
    const rowTotal = department
        ? categoryRows.reduce((s, r) => ({
            current: s.current + (r.run_rate?.current || 0),
            dec_aop: s.dec_aop + (r.run_rate?.dec_aop || 0),
            dec_fcst: s.dec_fcst + (r.run_rate?.dec_fcst || 0),
        }), { current: 0, dec_aop: 0, dec_fcst: 0 })
        : total;

    return (
        <DrillModalShell icon="🔁" title={label} sub={sub} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}>
            <div className="esd-table-wrap">
                <table className={`esd-table${!department ? ' esd-table-cellclick' : ''}`}>
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>{department ? 'Category' : 'Department'}</th>
                            <th>Current Run Rate</th>
                            <th>Dec AOP Run Rate</th>
                            <th>Dec Fcst Run Rate</th>
                            <th>Var to AOP RR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(d => (
                            <tr
                                key={d.key || d.category}
                                className={!department ? 'esd-row-drillable' : undefined}
                                onClick={!department ? () => onDrillDepartment?.(d.label) : undefined}
                                style={!department ? { cursor: 'pointer' } : undefined}
                            >
                                <td style={{ textAlign: 'left' }}>{d.label ? shortDeptLabel(d.label) : d.category}</td>
                                <td>{money(d.run_rate?.current)}</td>
                                <td>{money(d.run_rate?.dec_aop)}</td>
                                <td>{money(d.run_rate?.dec_fcst)}</td>
                                <td><VarianceStack actual={d.run_rate?.dec_fcst} base={d.run_rate?.dec_aop} /></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                            <td><strong>{money(rowTotal.current)}</strong></td>
                            <td><strong>{money(rowTotal.dec_aop)}</strong></td>
                            <td><strong>{money(rowTotal.dec_fcst)}</strong></td>
                            <td><VarianceStack actual={rowTotal.dec_fcst} base={rowTotal.dec_aop} /></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </DrillModalShell>
    );
}

// Sums every by_category row's own hc/nonhc/commission run_rate (whichever
// of the three a category actually carries — commission only exists for the
// 3 Sales dashboards, see global_views/summary.py) into one {hc, nonhc,
// commission} map, each holding its own full {current, dec_aop, dec_fcst}
// run_rate — the Run Rate equivalent of sumBySourceAndDepartment's
// dollar-figure split, just without a month axis (run_rate is always
// annualized, never scoped by quarter/YTD/custom-range). Always the page's
// whole (already department/category filtered via scopedData) scope — the
// company-wide entry point into the Run Rate drill (GlobalRunRateSplitModal
// below); the per-department/per-source step after it reads straight off
// GlobalRunRateRecordsModal's own records instead of re-summing this.
function sumRunRateBySource(byCategory) {
    const totals = { hc: null, nonhc: null, commission: null };
    Object.values(byCategory).forEach(row => {
        ['hc', 'nonhc', 'commission'].forEach(key => {
            const rr = row[key]?.run_rate;
            if (!rr) return;
            const existing = totals[key] || { current: 0, dec_aop: 0, dec_fcst: 0 };
            totals[key] = {
                current: existing.current + (rr.current || 0),
                dec_aop: existing.dec_aop + (rr.dec_aop || 0),
                dec_fcst: existing.dec_fcst + (rr.dec_fcst || 0),
            };
        });
    });
    return totals;
}

// One source's (hc/nonhc/commission) own run_rate, broken out one row per
// department, each still carrying its own full {current, dec_aop, dec_fcst}
// — same by_category grouping sumBySourceAndDepartment's dollar-figure
// sibling uses, summed once (not per month) since run_rate has no month
// axis to group by.
function runRateBySourceAndDepartment(byCategory, source) {
    const byDept = {};
    Object.values(byCategory).forEach(row => {
        const rr = row[source]?.run_rate;
        if (!rr) return;
        const existing = byDept[row.department] || { current: 0, dec_aop: 0, dec_fcst: 0 };
        byDept[row.department] = {
            current: existing.current + (rr.current || 0),
            dec_aop: existing.dec_aop + (rr.dec_aop || 0),
            dec_fcst: existing.dec_fcst + (rr.dec_fcst || 0),
        };
    });
    return Object.entries(byDept)
        .map(([department, rr]) => ({ department, ...rr }))
        .filter(r => r.current || r.dec_aop || r.dec_fcst)
        .sort((a, b) => b.current - a.current);
}

function sumRunRateRows(rows) {
    return rows.reduce((s, r) => ({ current: s.current + (r.current || 0), dec_aop: s.dec_aop + (r.dec_aop || 0), dec_fcst: s.dec_fcst + (r.dec_fcst || 0) }), { current: 0, dec_aop: 0, dec_fcst: 0 });
}

// Run Rate's own Headcount / Non-Headcount / Commission split — same role
// GlobalDeptHcNonHcModal plays for the dollar-figure Total KPI cards, and
// same Source | Current | Dec AOP | Dec FCST | Var to AOP table shape every
// department dashboard's own RunRateSplitModal already uses, just for
// Global's 3-way split instead of 2 (HC/Non-HC only). Always the
// company-wide entry point (the 3 page-level Run Rate KPI cards all open
// this one modal) — rows drill into GlobalRunRateSourceDeptModal via
// `onDrillSource`.
function GlobalRunRateSplitModal({ decLabel, data, title, sub, breadcrumb, onJump, onClose, onDrillSource }) {
    const sources = sumRunRateBySource(data.global_total.by_category);
    const rows = ['hc', 'nonhc', 'commission']
        .filter(key => sources[key] && (sources[key].current || sources[key].dec_aop || sources[key].dec_fcst))
        .map(key => ({ source: key, label: sourceLabel(key), subLabel: sourceSubLabel(key), ...sources[key] }));
    const total = sumRunRateRows(rows);
    return (
        <DrillModalShell icon="🔁" title={title} sub={sub} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose} maxWidth={860}>
            <div className="esd-table-wrap">
                <table className="esd-table esd-table-cellclick">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Source</th>
                            <th>Current Run Rate</th>
                            <th>Dec{decLabel} AOP Run Rate</th>
                            <th>Dec{decLabel} FCST Run Rate</th>
                            <th>Var to AOP RR and %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.source} onClick={() => onDrillSource(row.source, `${row.label} — ${title}`)} style={{ cursor: 'pointer' }}>
                                <td style={{ textAlign: 'left' }}>
                                    <div>{row.label}</div>
                                    <div style={{ fontSize: '0.82em', fontWeight: 500, color: 'var(--esd-muted, #65676b)' }}>{row.subLabel}</div>
                                </td>
                                <td><strong>{money(row.current)}</strong></td>
                                <td><strong>{money(row.dec_aop)}</strong></td>
                                <td><strong>{money(row.dec_fcst)}</strong></td>
                                <td><VarianceStack actual={row.dec_fcst} base={row.dec_aop} /></td>
                            </tr>
                        ))}
                        {!rows.length && <tr><td colSpan={5} className="esd-empty-row">No run rate data in this scope.</td></tr>}
                    </tbody>
                    {!!rows.length && (
                        <tfoot>
                            <tr>
                                <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                <td><strong>{money(total.current)}</strong></td>
                                <td><strong>{money(total.dec_aop)}</strong></td>
                                <td><strong>{money(total.dec_fcst)}</strong></td>
                                <td><VarianceStack actual={total.dec_fcst} base={total.dec_aop} /></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </DrillModalShell>
    );
}

// One level deeper than GlobalRunRateSplitModal — clicking Headcount/
// Non-Headcount/Commission there lands here: that one source's own run_rate,
// broken out one row per department, same table shape as every other Run
// Rate table in this file. Clicking a department pushes GlobalRunRateRecordsModal
// below — that one department's own records for this same source, the
// terminal step (same depth every department dashboard's own Run Rate split
// reaches before its own RunRateRecordsModal).
function GlobalRunRateSourceDeptModal({ source, decLabel, data, title, sub, breadcrumb, onJump, onClose, onDrillDepartment }) {
    const rows = runRateBySourceAndDepartment(data.global_total.by_category, source);
    const total = sumRunRateRows(rows);
    return (
        <DrillModalShell icon="🔁" title={title} sub={sub} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose} maxWidth={860}>
            <div className="esd-table-wrap">
                <table className="esd-table esd-table-cellclick">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Department</th>
                            <th>Current Run Rate</th>
                            <th>Dec{decLabel} AOP Run Rate</th>
                            <th>Dec{decLabel} FCST Run Rate</th>
                            <th>Var to AOP RR and %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.department} className="esd-row-drillable" onClick={() => onDrillDepartment(row.department)} style={{ cursor: 'pointer' }}>
                                <td style={{ textAlign: 'left' }}>{shortDeptLabel(row.department)}</td>
                                <td><strong>{money(row.current)}</strong></td>
                                <td><strong>{money(row.dec_aop)}</strong></td>
                                <td><strong>{money(row.dec_fcst)}</strong></td>
                                <td><VarianceStack actual={row.dec_fcst} base={row.dec_aop} /></td>
                            </tr>
                        ))}
                        {!rows.length && <tr><td colSpan={5} className="esd-empty-row">No department data in this scope.</td></tr>}
                    </tbody>
                    {!!rows.length && (
                        <tfoot>
                            <tr>
                                <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                <td><strong>{money(total.current)}</strong></td>
                                <td><strong>{money(total.dec_aop)}</strong></td>
                                <td><strong>{money(total.dec_fcst)}</strong></td>
                                <td><VarianceStack actual={total.dec_fcst} base={total.dec_aop} /></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </DrillModalShell>
    );
}

// Deepest Run Rate layer — every employee (Headcount) / vendor (Non-Headcount)
// / rep (Commission) record behind the one department+source a user drilled
// into, each already carrying its own annualized run_rate straight from the
// backend (global_views/raw.py attaches `calculated_run_rate` to every
// record it returns, same as every department's own /api/<dept>/raw/) — same
// convention and table shape as every department dashboard's own
// RunRateRecordsModal, just reading Global's /api/global/raw/ instead of a
// department-specific endpoint. Terminal — there's nothing to drill further
// into below an individual record.
function GlobalRunRateRecordsModal({ title, sub, department, source, breadcrumb, onJump, onClose }) {
    const [records, setRecords] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true); setError('');
        fetch(`${API_URL}/api/global/raw/?department=${encodeURIComponent(department)}&source=${source}`, { credentials: 'include' })
            .then(res => readApiJson(res, 'Could not load records.'))
            .then(body => { if (!cancelled) setRecords(body.records || []); })
            .catch(err => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [department, source]);

    const isHc = source === 'hc';
    const employeeFilters = useEmployeeDetailFilters(records || [], source);
    const valued = employeeFilters.records.map(r => ({
        ...r,
        _name: isHc ? (r.headcount_name_actual || r.headcount_name_aop || 'Unnamed employee') : (r.name || 'Unnamed vendor'),
        _sub: isHc ? (r.status || '') : (r.sub_category || ''),
    }));
    const sorted = [...valued].sort((a, b) => (b.run_rate?.current || 0) - (a.run_rate?.current || 0));
    const totals = sumRunRateRows(valued.map(r => r.run_rate || {}));

    return (
        <DrillModalShell icon={isHc ? '👥' : source === 'commission' ? '💰' : '📦'} title={title} sub={sub} maxWidth={900} breadcrumb={breadcrumb} onJump={onJump} onClose={onClose}>
            {!loading && !error && employeeFilters.controls}
            {error && <p style={{ padding: '8px 0', color: '#dc2626' }}>{error}</p>}
            {!error && (
                <div className="esd-table-wrap">
                    <table className="esd-table esd-table-static">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>{isHc ? 'Employee' : 'Name'}</th>
                                <th style={{ textAlign: 'left' }}>{isHc ? 'Status' : 'Sub-Category'}</th>
                                <th>Current Run Rate</th>
                                <th>Dec AOP Run Rate</th>
                                <th>Dec FCST Run Rate</th>
                                <th>Var to AOP RR and %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={6} className="esd-empty-row">Loading…</td></tr>}
                            {!loading && sorted.map(r => (
                                <tr key={r.id}>
                                    <td style={{ textAlign: 'left' }}>{r._name || '—'}</td>
                                    <td style={{ textAlign: 'left' }}>{r._sub || '—'}</td>
                                    <td>{money(r.run_rate?.current)}</td>
                                    <td>{money(r.run_rate?.dec_aop)}</td>
                                    <td>{money(r.run_rate?.dec_fcst)}</td>
                                    <td><VarianceStack actual={r.run_rate?.dec_fcst} base={r.run_rate?.dec_aop} /></td>
                                </tr>
                            ))}
                            {!loading && !sorted.length && (
                                <tr><td colSpan={6} className="esd-empty-row">No records in this scope.</td></tr>
                            )}
                        </tbody>
                        {!loading && !!sorted.length && (
                            <tfoot>
                                <tr>
                                    <td colSpan={2} style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                    <td><strong>{money(totals.current)}</strong></td>
                                    <td><strong>{money(totals.dec_aop)}</strong></td>
                                    <td><strong>{money(totals.dec_fcst)}</strong></td>
                                    <td><VarianceStack actual={totals.dec_fcst} base={totals.dec_aop} /></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}
        </DrillModalShell>
    );
}

export { GlobalDeepDiveModal, GlobalRunRateModal, GlobalRunRateSplitModal, GlobalRunRateSourceDeptModal, GlobalRunRateRecordsModal, GlobalPctDeepDiveModal, GlobalMonthSplitModal, GlobalDeptHcNonHcModal, GlobalSourceByDeptModal, GlobalCategoryBreakdownModal, GlobalRecordsModal };
